/**
 * AI Prompt Policy — SuperAdmin page
 *
 * Manage platform-level prompt rules that are injected before EVERY bot's system prompt.
 * SiteId = 0 → global defaults (Velaro manages). SiteId > 0 → per-tenant override.
 *
 * Route: /admin/ai-prompt-policy
 * Access: Velaro internal only (site 1032)
 */
import React, { useEffect, useState, useCallback } from "react";
import useApi from "@/hooks/useApi";
import AppLoading from "@/components/AppLoading";

interface AIPromptPolicy {
  id: number;
  siteId: number;
  key: string;
  label: string;
  content: string;
  enabled: boolean;
  sortOrder: number;
  overrideMode: string;
  updatedAt: string;
  updatedBy: string;
}

const KEY_SUGGESTIONS = [
  "tone_rules",
  "repetition_rules",
  "escalation_rules",
  "data_privacy_rules",
  "scope_rules",
];

const OVERRIDE_MODES: Record<string, string> = {
  additive: "Additive — append after global rule with same key",
  replace: "Replace — swap the global rule with this one",
  suppress: "Suppress — disable the global rule, use nothing",
};

function PolicyRow({
  policy,
  onSave,
  onDelete,
  onToggle,
}: {
  policy: AIPromptPolicy;
  onSave: (p: AIPromptPolicy) => void;
  onDelete: (id: number) => void;
  onToggle: (id: number, enabled: boolean) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(policy);
  const [saving, setSaving] = useState(false);

  function startEdit() {
    setDraft({ ...policy });
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    await onSave(draft);
    setSaving(false);
    setEditing(false);
  }

  return (
    <div
      className={`border rounded-lg mb-2 overflow-hidden transition-colors ${
        policy.enabled ? "border-slate-200" : "border-slate-100 opacity-60"
      }`}
    >
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-white">
        <input
          type="checkbox"
          checked={policy.enabled}
          onChange={(e) => onToggle(policy.id, e.target.checked)}
          className="w-4 h-4 shrink-0"
          aria-label={`Enable ${policy.label}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{policy.label}</span>
            <code className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">
              {policy.key}
            </code>
            {policy.siteId === 0 && (
              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                Global
              </span>
            )}
            {policy.siteId > 0 && (
              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                Site {policy.siteId} override ({policy.overrideMode})
              </span>
            )}
            <span className="text-xs text-slate-400">order: {policy.sortOrder}</span>
          </div>
          {!editing && (
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{policy.content}</p>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            type="button"
            onClick={startEdit}
            className="text-xs px-2 py-1 rounded border border-blue-200 text-blue-600 hover:bg-blue-50"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => onDelete(policy.id)}
            className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Inline editor */}
      {editing && (
        <div className="px-4 pb-4 pt-2 bg-slate-50 border-t border-slate-200 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Label</label>
              <input
                type="text"
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">
                Key <span className="text-slate-400">(snake_case)</span>
              </label>
              <input
                type="text"
                list="key-suggestions"
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 font-mono"
                value={draft.key}
                onChange={(e) => setDraft({ ...draft, key: e.target.value })}
              />
              <datalist id="key-suggestions">
                {KEY_SUGGESTIONS.map((k) => (
                  <option key={k} value={k} />
                ))}
              </datalist>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">
              Prompt Content{" "}
              <span className="text-slate-400">— injected verbatim into system prompt</span>
            </label>
            <textarea
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 font-mono"
              rows={5}
              value={draft.content}
              onChange={(e) => setDraft({ ...draft, content: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Sort Order</label>
              <input
                type="number"
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                value={draft.sortOrder}
                onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })}
              />
            </div>
            {draft.siteId > 0 && (
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-700 block mb-1">
                  Override Mode
                </label>
                <select
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                  value={draft.overrideMode}
                  onChange={(e) => setDraft({ ...draft, overrideMode: e.target.value })}
                >
                  {Object.entries(OVERRIDE_MODES).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NewPolicyForm({
  siteId,
  onCreated,
}: {
  siteId: number;
  onCreated: (p: AIPromptPolicy) => void;
}) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Partial<AIPromptPolicy>>({
    siteId,
    key: "",
    label: "",
    content: "",
    enabled: true,
    sortOrder: 100,
    overrideMode: "additive",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function create() {
    if (!form.key?.trim() || !form.label?.trim() || !form.content?.trim()) {
      setError("Key, Label, and Content are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const endpoint = siteId === 0 ? "AIPromptPolicy" : "AIPromptPolicy/Override";
      const resp = await api.messaging.post(endpoint, { ...form, siteId });
      if (resp.ok) {
        const created: AIPromptPolicy = await resp.json();
        onCreated(created);
        setOpen(false);
        setForm({ siteId, key: "", label: "", content: "", enabled: true, sortOrder: 100, overrideMode: "additive" });
      } else {
        setError("Failed to create policy.");
      }
    } catch {
      setError("Network error.");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full text-sm px-3 py-2 rounded border border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 mt-2"
      >
        + Add {siteId === 0 ? "Global" : `Site ${siteId}`} Policy
      </button>
    );
  }

  return (
    <div className="border border-blue-200 rounded-lg p-4 bg-blue-50 mt-2 space-y-3">
      <div className="text-sm font-semibold text-blue-900">
        New {siteId === 0 ? "Global" : `Site ${siteId} Override`} Policy
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-700 block mb-1">Label</label>
          <input
            type="text"
            className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="e.g. Tone & Professionalism"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-700 block mb-1">Key</label>
          <input
            type="text"
            list="key-suggestions-new"
            className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 font-mono bg-white"
            value={form.key}
            onChange={(e) => setForm({ ...form, key: e.target.value })}
            placeholder="e.g. tone_rules"
          />
          <datalist id="key-suggestions-new">
            {KEY_SUGGESTIONS.map((k) => (
              <option key={k} value={k} />
            ))}
          </datalist>
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-700 block mb-1">Prompt Content</label>
        <textarea
          className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 font-mono bg-white"
          rows={4}
          value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="The prompt fragment injected before the bot's system prompt…"
        />
      </div>
      <div className="flex items-center gap-4">
        <div>
          <label className="text-xs font-medium text-slate-700 block mb-1">Sort Order</label>
          <input
            type="number"
            className="w-24 text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
            value={form.sortOrder}
            onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
          />
        </div>
        {siteId > 0 && (
          <div className="flex-1">
            <label className="text-xs font-medium text-slate-700 block mb-1">Override Mode</label>
            <select
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 bg-white"
              value={form.overrideMode}
              onChange={(e) => setForm({ ...form, overrideMode: e.target.value })}
            >
              {Object.entries(OVERRIDE_MODES).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-sm px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={create}
          disabled={saving}
          className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? "Creating…" : "Create Policy"}
        </button>
      </div>
    </div>
  );
}

export default function AIPromptPolicyPage() {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [globalPolicies, setGlobalPolicies] = useState<AIPromptPolicy[]>([]);
  const [siteId, setSiteId] = useState<number>(0);
  const [sitePolicies, setSitePolicies] = useState<AIPromptPolicy[]>([]);
  const [error, setError] = useState("");

  const fetchGlobal = useCallback(async () => {
    try {
      const resp = await api.messaging.get("AIPromptPolicy/Global");
      if (resp.ok) setGlobalPolicies(await resp.json());
    } catch {
      setError("Failed to load global policies.");
    }
  }, [api.messaging]);

  const fetchSite = useCallback(async (id: number) => {
    if (!id) return;
    try {
      const resp = await api.messaging.get(`AIPromptPolicy/Site/${id}`);
      if (resp.ok) setSitePolicies(await resp.json());
    } catch { /* ignored */ }
  }, [api.messaging]);

  useEffect(() => {
    (async () => {
      await fetchGlobal();
      setLoading(false);
    })();
  }, [fetchGlobal]);

  async function savePolicy(updated: AIPromptPolicy) {
    try {
      const resp = await api.messaging.put(`AIPromptPolicy/${updated.id}`, updated);
      if (resp.ok) {
        const saved: AIPromptPolicy = await resp.json();
        if (saved.siteId === 0) {
          setGlobalPolicies((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
        } else {
          setSitePolicies((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
        }
      }
    } catch { /* error handling via error boundary */ }
  }

  async function deletePolicy(id: number) {
    if (!confirm("Delete this policy? This cannot be undone.")) return;
    try {
      const resp = await api.messaging.delete(`AIPromptPolicy/${id}`);
      if (resp.ok) {
        setGlobalPolicies((prev) => prev.filter((p) => p.id !== id));
        setSitePolicies((prev) => prev.filter((p) => p.id !== id));
      }
    } catch { /* error handling via error boundary */ }
  }

  async function togglePolicy(id: number, enabled: boolean) {
    const policy = [...globalPolicies, ...sitePolicies].find((p) => p.id === id);
    if (!policy) return;
    await savePolicy({ ...policy, enabled });
  }

  if (loading) return <AppLoading />;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">AI Prompt Policies</h1>
        <p className="text-slate-600 text-sm max-w-2xl">
          Platform-level rules injected at the TOP of every AI bot's system prompt, before the
          Global Prompt → Bot Prompt → Channel Override layers. Manage universal behaviors like
          tone, repetition prevention, escalation rules, and data privacy — without redeploying.
        </p>
        <div className="mt-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
          <strong>Injection order:</strong> Platform Rules (here) → Global Prompt (Account &gt; AI
          Settings) → Bot Prompt (Bot Config) → Channel Override (per-channel)
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Global Policies */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Global Policies</h2>
            <p className="text-xs text-slate-500">
              Apply to ALL tenants. Enabled by default when the table is seeded at startup.
            </p>
          </div>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
            {globalPolicies.filter((p) => p.enabled).length} / {globalPolicies.length} active
          </span>
        </div>

        {globalPolicies.length === 0 ? (
          <p className="text-sm text-slate-400 italic">
            No global policies found. They are seeded at app startup — restart the app or add one
            below.
          </p>
        ) : (
          globalPolicies.map((p) => (
            <PolicyRow
              key={p.id}
              policy={p}
              onSave={savePolicy}
              onDelete={deletePolicy}
              onToggle={togglePolicy}
            />
          ))
        )}

        <NewPolicyForm
          siteId={0}
          onCreated={(p) => setGlobalPolicies((prev) => [...prev, p])}
        />
      </div>

      {/* Per-Site Overrides */}
      <div>
        <h2 className="text-lg font-semibold text-slate-800 mb-1">Per-Site Overrides</h2>
        <p className="text-xs text-slate-500 mb-3">
          Override or suppress a global policy for a specific tenant. Enter a Site ID to load and
          manage that site's overrides.
        </p>
        <div className="flex gap-2 mb-4">
          <input
            type="number"
            className="w-32 text-sm border border-slate-300 rounded px-2 py-1.5"
            placeholder="Site ID"
            value={siteId || ""}
            onChange={(e) => setSiteId(Number(e.target.value))}
          />
          <button
            type="button"
            onClick={() => fetchSite(siteId)}
            className="text-sm px-3 py-1.5 rounded bg-slate-700 text-white hover:bg-slate-800"
          >
            Load Site
          </button>
        </div>

        {siteId > 0 && (
          <>
            {sitePolicies.length === 0 ? (
              <p className="text-sm text-slate-400 italic">No overrides for site {siteId} yet.</p>
            ) : (
              sitePolicies.map((p) => (
                <PolicyRow
                  key={p.id}
                  policy={p}
                  onSave={savePolicy}
                  onDelete={deletePolicy}
                  onToggle={togglePolicy}
                />
              ))
            )}
            <NewPolicyForm
              siteId={siteId}
              onCreated={(p) => setSitePolicies((prev) => [...prev, p])}
            />
          </>
        )}
      </div>
    </div>
  );
}
